import React, { useState, useRef, useEffect, useMemo, useContext, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa'; // Make sure to install react-icons
import { UserContext } from '../../context/userContext';
import {
    SearchContainer,
    SearchBar,
    SearchBox,
    SearchHeading,
    CategoryDiv,
    PropertyName,
    AgentName,
    SearchItem,
    SearchItemInner,
    NoSearchDiv,
    NoSearchText,
} from './search-bar.styles';

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 250;

const Search = ({
    placeholder = 'Search',
    resultFilter,
    onResultSelect,
    searchBarWidth,
    dropdownWidth,
    dropdownTop,
    dropdownHeight,
    dropdownMaxHeight,
    dropdownPortal = false,
    dropdownOffset = 8,
    noResultsText = 'No items found',
    loadingText = 'Searching...',
    className,
    autoFocus = false,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [portalDropdownPosition, setPortalDropdownPosition] = useState(null);
    const containerRef = useRef(null);
    const dropdownRef = useRef(null);
    const debounceTimerRef = useRef(null);
    const abortControllerRef = useRef(null);
    const resultCacheRef = useRef(new Map());
    const latestQueryRef = useRef('');
    const navigate = useNavigate();
    const { accessToken } = useContext(UserContext);
    const trimmedSearchTerm = (searchTerm || '').trim();
    const shouldShowDropdown = showSearch && trimmedSearchTerm.length >= MIN_QUERY_LENGTH;

    const updatePortalDropdownPosition = useCallback(() => {
        if (!dropdownPortal || !containerRef.current || typeof window === 'undefined') {
            return;
        }

        const rect = containerRef.current.getBoundingClientRect();
        const top = rect.bottom + dropdownOffset;
        const availableHeight = Math.max(160, window.innerHeight - top - 16);
        const resolveHeight = (height, fallback) => {
            if (!height) return fallback;
            const pixelHeight =
                typeof height === 'string' ? height.match(/^(\d+(?:\.\d+)?)px$/) : null;
            if (pixelHeight) {
                return `${Math.min(Number(pixelHeight[1]), availableHeight)}px`;
            }
            return height;
        };

        setPortalDropdownPosition({
            left: `${rect.left}px`,
            top: `${top}px`,
            width: dropdownWidth && dropdownWidth !== '100%' ? dropdownWidth : `${rect.width}px`,
            maxHeight: resolveHeight(dropdownMaxHeight, `${Math.min(320, availableHeight)}px`),
        });
    }, [dropdownMaxHeight, dropdownOffset, dropdownPortal, dropdownWidth]);

    useEffect(() => {
        function handleClickOutside(event) {
            const isInsideSearch = containerRef.current?.contains(event.target);
            const isInsideDropdown = dropdownRef.current?.contains(event.target);

            if (!isInsideSearch && !isInsideDropdown) {
                setShowSearch(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (!dropdownPortal || !shouldShowDropdown) {
            return undefined;
        }

        updatePortalDropdownPosition();
        window.addEventListener('resize', updatePortalDropdownPosition);
        window.addEventListener('scroll', updatePortalDropdownPosition, true);

        return () => {
            window.removeEventListener('resize', updatePortalDropdownPosition);
            window.removeEventListener('scroll', updatePortalDropdownPosition, true);
        };
    }, [dropdownPortal, shouldShowDropdown, updatePortalDropdownPosition]);

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    useEffect(() => {
        const query = (searchTerm || '').trim();
        latestQueryRef.current = query;

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        if (query.length < MIN_QUERY_LENGTH || !accessToken) {
            setIsLoading(false);
            setResults([]);
            setShowSearch(false);
            return;
        }

        const cached = resultCacheRef.current.get(query.toLowerCase());
        if (cached) {
            updatePortalDropdownPosition();
            setResults(cached);
            setShowSearch(true);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        updatePortalDropdownPosition();
        setShowSearch(true);

        debounceTimerRef.current = setTimeout(async () => {
            const controller = new AbortController();
            abortControllerRef.current = controller;

            try {
                const response = await fetch(
                    `/api/search/search?string=${encodeURIComponent(query)}`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${accessToken}`,
                        },
                        signal: controller.signal,
                    }
                );

                if (!response.ok) {
                    throw new Error(`Search request failed with status ${response.status}`);
                }

                const data = await response.json();
                const normalized = Array.isArray(data) ? data : [];
                const visibleResults = resultFilter ? normalized.filter(resultFilter) : normalized;
                resultCacheRef.current.set(query.toLowerCase(), visibleResults);

                if (latestQueryRef.current === query) {
                    setResults(visibleResults);
                }
            } catch (error) {
                if (error?.name !== 'AbortError' && latestQueryRef.current === query) {
                    setResults([]);
                }
            } finally {
                if (latestQueryRef.current === query) {
                    setIsLoading(false);
                }
            }
        }, SEARCH_DEBOUNCE_MS);
    }, [accessToken, searchTerm, resultFilter, updatePortalDropdownPosition]);

    const handleChange = (event) => {
        const query = event.target.value;
        setSearchTerm(query);
        if (query.trim().length >= MIN_QUERY_LENGTH) {
            updatePortalDropdownPosition();
        }
    };

    const handleFocus = () => {
        const query = (searchTerm || '').trim();
        if (query.length >= MIN_QUERY_LENGTH) {
            setShowSearch(true);
            updatePortalDropdownPosition();
        }
    };

    const handleResultClick = (result) => {
        if (onResultSelect) {
            onResultSelect(result);
        } else {
            navigate(result.url);
        }
        setShowSearch(false);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            setShowSearch(false);
        }
        if (event.key === 'Enter' && results.length > 0) {
            handleResultClick(results[0]);
        }
    };

    const groupedResults = useMemo(() => {
        return results.reduce((acc, result) => {
            (acc[result.type] = acc[result.type] || []).push(result);
            return acc;
        }, {});
    }, [results]);

    const canRenderDropdown = shouldShowDropdown && (!dropdownPortal || portalDropdownPosition);

    const dropdown = canRenderDropdown ? (
        <SearchBox
            ref={dropdownRef}
            $width={dropdownPortal ? portalDropdownPosition?.width : dropdownWidth}
            $top={dropdownPortal ? portalDropdownPosition?.top : dropdownTop}
            $left={dropdownPortal ? portalDropdownPosition?.left : undefined}
            $height={dropdownHeight}
            $maxHeight={dropdownPortal ? portalDropdownPosition?.maxHeight : dropdownMaxHeight}
            $position={dropdownPortal ? 'fixed' : undefined}
        >
            {isLoading ? (
                <NoSearchDiv>
                    <NoSearchText>{loadingText}</NoSearchText>
                </NoSearchDiv>
            ) : Object.entries(groupedResults).length > 0 ? (
                Object.entries(groupedResults).map(([type, results]) => (
                    <CategoryDiv key={type}>
                        <SearchHeading>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                        </SearchHeading>
                        <SearchItem>
                            {results.map((result) => (
                                <SearchItemInner
                                    key={result.id}
                                    onClick={() => handleResultClick(result)}
                                >
                                    <PropertyName>{result.name}</PropertyName>
                                    {result.agentName && (
                                        <AgentName>
                                            Agent: <strong>{result.agentName}</strong>
                                        </AgentName>
                                    )}
                                </SearchItemInner>
                            ))}
                        </SearchItem>
                    </CategoryDiv>
                ))
            ) : (
                <NoSearchDiv>
                    <NoSearchText>{noResultsText}</NoSearchText>
                </NoSearchDiv>
            )}
        </SearchBox>
    ) : null;

    return (
        <SearchContainer ref={containerRef} className={className}>
            <SearchBar $width={searchBarWidth}>
                <FaSearch color="#aaa" /> {/* Adjust color as needed */}
                <input
                    type="text"
                    placeholder={placeholder}
                    value={searchTerm}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onKeyDown={handleKeyDown}
                    autoFocus={autoFocus}
                />
            </SearchBar>
            {dropdownPortal && dropdown && typeof document !== 'undefined'
                ? createPortal(dropdown, document.body)
                : dropdown}
        </SearchContainer>
    );
};

export default Search;
